import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Card, CardList, EmptyState, PageHeader } from '@/design/components'
import { useMe } from '@/app/MeContextProvider'
import { useBreadcrumb } from '@/app/Breadcrumb'
import { plural } from '@/lib/format'
import { cn } from '@/lib/cn'
import { DeadlineRow } from '../components/DeadlineRow'
import { EditDeadlineModal } from '../components/EditDeadlineModal'
import { NewDeadlineModal } from '../components/NewDeadlineModal'
import { useDeadlines } from '../hooks'
import { isClosed } from '../status'
import type { Deadline } from '../types'

/**
 * Sekcje ekranu: zaległe, krytyczne (do 3 dni), bieżący miesiąc, dalsze.
 *
 * „Zaległe" są WYDZIELONE z sekcji krytycznej: wcześniej termin po upływie wpadał do
 * „Krytyczne — do 3 dni", więc nagłówek kłamał (7 dni po terminie to nie „do 3 dni"),
 * a kancelaria nie widziała skali zaległości. Ten sam podział ma `GET /api/deadlines/stats`
 * (`overdue` osobno, `critical` jako podzbiór `next7`).
 */
type GroupKey = 'overdue' | 'critical' | 'month' | 'later' | 'closed'

const GROUPS: { key: GroupKey; label: string; dot: string }[] = [
  { key: 'overdue', label: 'Po terminie — zaległe', dot: 'bg-danger' },
  { key: 'critical', label: 'Krytyczne — do 3 dni', dot: 'bg-danger' },
  { key: 'month', label: 'W tym miesiącu', dot: 'bg-warning' },
  { key: 'later', label: 'Dalsze', dot: 'bg-navy-800' },
  { key: 'closed', label: 'Zamknięte', dot: 'bg-[#b7c3cc]' },
]

function groupOf(deadline: Deadline, today: Date): GroupKey {
  // Termin zakończony/anulowany nie jest ani zaległy, ani krytyczny — trafiał wcześniej
  // do „Krytyczne — do 3 dni" wyłącznie dlatego, że data już minęła.
  if (isClosed(deadline.status)) return 'closed'
  if (deadline.days_left < 0) return 'overdue'
  if (deadline.days_left <= 3) return 'critical'
  const due = new Date(deadline.due_date)
  const sameMonth =
    due.getFullYear() === today.getFullYear() && due.getMonth() === today.getMonth()
  return sameMonth ? 'month' : 'later'
}

export function DeadlinesPage() {
  const { can } = useMe()
  useBreadcrumb(['Obieg dokumentów', 'Terminy procesowe'])

  const [newOpen, setNewOpen] = useState(false)
  const [selected, setSelected] = useState<Deadline | null>(null)

  const listing = useDeadlines({ size: 200 })
  const canManage = can('deadlines.manage')

  // Wejście z karty sprawy (`/terminy?termin=<id>`) otwiera od razu ten termin i czyści
  // query param — wzorzec `?nowa=1` z modułów `tasks` i `timesheet`. Moduł `cases` nie
  // ma dostępu do modala terminu (kontrakt §4), więc przekazuje nam id w URL.
  const [searchParams, setSearchParams] = useSearchParams()
  const wanted = Number(searchParams.get('termin'))
  const items = listing.data?.items
  useEffect(() => {
    if (!Number.isInteger(wanted) || wanted <= 0 || !items) return
    const found = items.find((deadline) => deadline.id === wanted)
    if (!found) return
    const next = new URLSearchParams(searchParams)
    next.delete('termin')
    setSearchParams(next, { replace: true })
    setSelected(found)
  }, [wanted, items, searchParams, setSearchParams])

  const grouped = useMemo(() => {
    const today = new Date()
    const buckets: Record<GroupKey, Deadline[]> = {
      overdue: [],
      critical: [],
      month: [],
      later: [],
      closed: [],
    }
    for (const deadline of listing.data?.items ?? []) {
      buckets[groupOf(deadline, today)].push(deadline)
    }
    return buckets
  }, [listing.data])

  const total = listing.data?.total ?? 0

  return (
    <>
      <PageHeader
        title="Terminy procesowe"
        subtitle="Terminy wykryte w pismach oraz wprowadzone ręcznie, uporządkowane wg daty upływu — zaległe na górze."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setNewOpen(true)}>
              Dodaj termin
            </Button>
          ) : null
        }
      />

      {listing.isPending ? (
        <Card>
          <div className="divide-y divide-line-soft">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-4 px-5 py-4">
                <span className="h-3 w-20 animate-pulse rounded bg-line-soft" />
                <span className="h-3 w-1/3 animate-pulse rounded bg-line-soft" />
              </div>
            ))}
          </div>
        </Card>
      ) : total === 0 ? (
        <Card>
          <EmptyState
            title="Brak terminów"
            description="Terminy pojawią się tu po weryfikacji pism albo po ręcznym dodaniu."
            action={
              canManage ? (
                <Button variant="primary" onClick={() => setNewOpen(true)}>
                  Dodaj termin
                </Button>
              ) : null
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          {GROUPS.map((group) => {
            const items = grouped[group.key]
            if (items.length === 0) return null
            return (
              <section key={group.key}>
                <header className="mb-2 flex items-center gap-2.5">
                  <i className={cn('h-[9px] w-[9px] rounded-[2px]', group.dot)} aria-hidden />
                  <h2 className="t-label text-ink">{group.label}</h2>
                  <span className="text-small text-muted">
                    {items.length} {plural(items.length, 'termin', 'terminy', 'terminów')}
                  </span>
                </header>
                <Card>
                  <CardList>
                    {items.map((deadline) => (
                      <DeadlineRow
                        key={deadline.id}
                        deadline={deadline}
                        onClick={() => setSelected(deadline)}
                      />
                    ))}
                  </CardList>
                </Card>
              </section>
            )
          })}
        </div>
      )}

      <NewDeadlineModal open={newOpen} onClose={() => setNewOpen(false)} />
      <EditDeadlineModal
        deadline={selected}
        canManage={canManage}
        onClose={() => setSelected(null)}
      />
    </>
  )
}
